import React, { useEffect, useMemo, useRef, useState } from 'react';
import marketplacesettingsService from '../../services/marketplacesettingsService';
import './CredentialOverrides.css';

// Per-instance override entry for a selected credential — the fields a marketplace flags as
// "must be overridden". When a chain/partner-level credential is reused for a specific hotel /
// vault / payment-porter, these fields (e.g. HOTEL_ID, and any other must_be_overridden setting)
// must be supplied for that instance rather than inherited from the shared credential.
//
// Single source of truth shared by payment porter, vault and sync so the override entry looks and
// behaves identically everywhere — a sibling of CredentialsPicker / CredentialChip, never re-implemented.
//
// Props:
//   marketplaceId  — used to load the setting definitions when `defs` is not supplied
//   level          — the selected credential's entity level ('hotel'|'account'|'partner'|'hconnect'|null)
//   activeLevels   — levels that require overrides (default ['account','partner']; vault API adds 'hconnect')
//   values         — { [SETTING_NAME]: string } current override values, keyed by marketplace setting name
//   onChange       — (settingName, value) => void
//   defs           — optional pre-loaded setting definitions (uppercase or lowercase shape); skips the fetch
//   onDefsChange   — optional (normalizedDefs[]) => void, so the parent can drive validation
//   disabled, title, className

const HOTEL_ID_KEYS = ['HOTELID', 'HOTEL_ID'];

const normalizeDef = (d) => {
    const name = d.name || d.setting_name || d.SETTINGNAME;
    return {
        name,
        label: d.label || d.setting_label || d.SETTINGLABEL || name,
        dataType: String(d.dataType || d.data_type || d.DATATYPE || '').toUpperCase(),
        placeholder: d.placeholder || d.PLACEHOLDER || '',
        helper: d.helper || d.helper_text || d.HELPERTEXT || '',
        isHotelId: HOTEL_ID_KEYS.includes(String(name || '').toUpperCase()),
    };
};

// Normalize an arbitrary settings list to the must-be-overridden defs, tolerant of both the
// uppercase getAllSettings shape and the lowercase picker-schema shape.
export const normalizeOverrideDefs = (list = []) =>
    list
        .filter(d => String(d.must_be_overridden ?? d.MUST_BE_OVERRIDDEN ?? 'N').toLowerCase() === 'y')
        .map(normalizeDef)
        .filter(d => d.name);

// True when every override field has a non-empty value. `defs` is the override subset — either the
// component's normalized defs or a pre-filtered must-be-overridden settings list (any key shape).
export const overridesComplete = (defs = [], values = {}) =>
    defs.every(d => String(values[d.name || d.setting_name || d.SETTINGNAME] ?? '').trim());

export default function CredentialOverrides({
    marketplaceId,
    level,
    activeLevels = ['account', 'partner'],
    values = {},
    onChange,
    defs: providedDefs,
    onDefsChange,
    disabled = false,
    title = 'Per-Hotel Overrides',
    className = '',
}) {
    const [loadedDefs, setLoadedDefs] = useState([]);
    const active = !!level && activeLevels.includes(level);

    // Self-load definitions when the parent hasn't supplied them, so a consumer only needs to pass
    // a marketplaceId to get the correct override fields.
    useEffect(() => {
        if (providedDefs || !marketplaceId) return;
        let cancelled = false;
        marketplacesettingsService.getSchema({ MARKETPLACEID: marketplaceId })
            .then(res => { if (!cancelled) setLoadedDefs(normalizeOverrideDefs(res?.settings || [])); })
            .catch(() => { if (!cancelled) setLoadedDefs([]); });
        return () => { cancelled = true; };
    }, [providedDefs, marketplaceId]);

    const defs = useMemo(
        () => (providedDefs ? normalizeOverrideDefs(providedDefs) : loadedDefs),
        [providedDefs, loadedDefs]
    );

    // Report defs to the parent only when the field set actually changes (guards against a re-render loop).
    const sig = defs.map(d => d.name).join('|');
    const lastSig = useRef(null);
    useEffect(() => {
        if (lastSig.current === sig) return;
        lastSig.current = sig;
        onDefsChange && onDefsChange(defs);
    }, [sig]); // eslint-disable-line

    if (!active || defs.length === 0) return null;

    return (
        <div className={`cred-overrides ${className}`.trim()}>
            <div className="cred-overrides-title">{title}</div>
            {defs.map(def => (
                <div key={def.name} className="cred-overrides-field">
                    <label className="cred-overrides-label">
                        {def.label} <span className="cred-overrides-req">*</span>
                        {def.isHotelId && <span className="cred-overrides-hint"> — the property code used in the PMS for this hotel</span>}
                    </label>
                    <input
                        className="cred-overrides-input"
                        type={def.dataType === 'PASSWORD' ? 'password' : 'text'}
                        autoComplete={def.dataType === 'PASSWORD' ? 'new-password' : 'off'}
                        placeholder={def.placeholder || (def.isHotelId ? 'e.g. LONHILTON' : '')}
                        value={values[def.name] || ''}
                        disabled={disabled}
                        onChange={e => onChange && onChange(def.name, e.target.value)}
                    />
                    {def.helper && <span className="cred-overrides-help">{def.helper}</span>}
                </div>
            ))}
        </div>
    );
}
